import { highlightText } from '~/shared/lib/highlight-text'
import { Text } from '~/shared/ui'
import type {
  AdjustmentDiffData,
  CheckedPatientDiffData,
  EobProcessingEventProps,
  PatientClaimDiffData,
} from '../../model'
import { CollapsibleSection, EventNotes, FieldsGrid } from '../shared'

/** Render all sections for a single patient claim */
function PatientClaimSections({
  claim,
  searchQuery,
  userRole,
}: {
  claim: PatientClaimDiffData
  searchQuery: string
  userRole: 'person' | 'opti'
}) {
  const { contextLine } = claim

  return (
    <>
      {[...claim.serviceLines].reverse().map((sl, i, reversed) => {
        if (sl.fields.length === 0) return null

        const originalIndex = reversed.length - i
        const slStatusLabel =
          claim.status !== 'removed' && sl.status === 'removed' ? ' (Removed)' : ''

        return (
          <CollapsibleSection
            key={sl.serviceLineId}
            title={`${contextLine} – Service line ${originalIndex}${slStatusLabel}`}
            searchQuery={searchQuery}
          >
            <FieldsGrid
              className="mt-1"
              fields={sl.fields}
              searchQuery={searchQuery}
              userRole={userRole}
              dataLabel="Reviewed Data"
            />
          </CollapsibleSection>
        )
      })}
      {claim.claimFields.length > 0 && (
        <CollapsibleSection
          title={`${contextLine} – Patient Claim Data`}
          searchQuery={searchQuery}
        >
          <FieldsGrid
            className="mt-1"
            fields={claim.claimFields}
            searchQuery={searchQuery}
            userRole={userRole}
            dataLabel="Reviewed Data"
          />
        </CollapsibleSection>
      )}
    </>
  )
}

/** Render adjustment diff sections */
function AdjustmentDiffSections({
  diffs,
  searchQuery,
  userRole,
}: {
  diffs: AdjustmentDiffData[]
  searchQuery: string
  userRole: 'person' | 'opti'
}) {
  return (
    <>
      {diffs.map((adj, i) => {
        if (adj.fields.length === 0) return null
        const statusLabel = adj.status === 'removed' ? ' (Removed)' : ''
        return (
          <CollapsibleSection
            key={adj.adjustmentKey}
            title={`Payment adjustment ${i + 1}${statusLabel}`}
            searchQuery={searchQuery}
          >
            <FieldsGrid
              className="mt-1"
              fields={adj.fields}
              searchQuery={searchQuery}
              userRole={userRole}
              dataLabel="Reviewed Data"
            />
          </CollapsibleSection>
        )
      })}
    </>
  )
}

/** Render checked patient diff sections */
function CheckedPatientDiffSections({
  diffs,
  searchQuery,
  userRole,
}: {
  diffs: CheckedPatientDiffData[]
  searchQuery: string
  userRole: 'person' | 'opti'
}) {
  return (
    <>
      {diffs.map((cp, i) => {
        if (cp.fields.length === 0) return null
        const statusLabel = cp.status === 'removed' ? ' (Removed)' : ''
        return (
          <CollapsibleSection
            key={cp.patientKey}
            title={`Checked Patient ${i + 1}${statusLabel}`}
            searchQuery={searchQuery}
          >
            <FieldsGrid
              className="mt-1"
              fields={cp.fields}
              searchQuery={searchQuery}
              userRole={userRole}
              dataLabel="Reviewed Data"
            />
          </CollapsibleSection>
        )
      })}
    </>
  )
}

export function EobProcessingEvent({
  event,
  searchQuery,
}: {
  event: EobProcessingEventProps
  searchQuery: string
}) {
  return (
    <>
      <div className="flex flex-col gap-0.5 py-[3px]">
        <Text appearance="SUBTITLE2">{highlightText(event.title, searchQuery)}</Text>
        {event.fields ? (
          <FieldsGrid
            fields={event.fields}
            searchQuery={searchQuery}
            userRole={event.userRole}
            dataLabel="Reviewed Data"
          />
        ) : null}
        {event.adjustmentDiffs ? (
          <AdjustmentDiffSections
            diffs={event.adjustmentDiffs}
            searchQuery={searchQuery}
            userRole={event.userRole}
          />
        ) : null}
        {event.checkedPatientDiffs ? (
          <CheckedPatientDiffSections
            diffs={event.checkedPatientDiffs}
            searchQuery={searchQuery}
            userRole={event.userRole}
          />
        ) : null}
        {event.patientClaims?.map((claim) => (
          <PatientClaimSections
            key={claim.claimKey}
            claim={claim}
            searchQuery={searchQuery}
            userRole={event.userRole}
          />
        ))}
      </div>
      {event.notes && event.userRole === 'person' && (
        <EventNotes
          notes={event.notes}
          searchQuery={searchQuery}
        />
      )}
    </>
  )
}
